/**
 * Desktop notifications from an open Cai tab, for news that lands while the reader is looking elsewhere.
 *
 * Page notifications, not push: they need the tab open. The browser asks for permission only from a
 * click, and only once. After that a notice shows only while the reader is away from the tab, since the
 * page already shows the news to someone looking at it, and it closes once they are back.
 */

export enum NotificationPermissionState {
  /** No notifications here: the browser has none, it is Chrome on Android, or the page is in a frame. */
  UNSUPPORTED = 'unsupported',
  /** The browser hasn't asked yet. */
  DEFAULT = 'default',
  GRANTED = 'granted',
  DENIED = 'denied',
}

const permissionListeners = new Set<() => void>()

function supported(): boolean {
  if (typeof window === 'undefined' || !('Notification' in window)) return false
  // Chrome on Android shows them only from a service worker, so constructing one there throws.
  if (/Android/i.test(navigator.userAgent)) return false
  // Browsers refuse to ask from inside another site's frame, such as a broker's embedded widget.
  return window.top === window.self
}

export function getNotificationPermission(): NotificationPermissionState {
  if (!supported()) return NotificationPermissionState.UNSUPPORTED
  return Notification.permission as NotificationPermissionState
}

/** Call it from a click: browsers ignore or block a request made without one. */
export async function requestNotificationPermission(): Promise<NotificationPermissionState> {
  if (!supported()) return NotificationPermissionState.UNSUPPORTED
  try {
    await Notification.requestPermission()
  } catch {
    // Reading the permission afterwards says where it ended either way.
  }
  permissionListeners.forEach((listener) => listener())
  return getNotificationPermission()
}

/** For useSyncExternalStore. A change made in the browser's site settings shows once the tab has focus again. */
export function subscribeToNotificationPermission(onChange: () => void): () => void {
  permissionListeners.add(onChange)
  window.addEventListener('focus', onChange)
  document.addEventListener('visibilitychange', onChange)
  return () => {
    permissionListeners.delete(onChange)
    window.removeEventListener('focus', onChange)
    document.removeEventListener('visibilitychange', onChange)
  }
}

/** Hidden behind another tab, or visible in a window that isn't the one in use. */
export function isReaderAway(): boolean {
  return document.visibilityState === 'hidden' || !document.hasFocus()
}

/** Runs onReturn, once, when the reader is next back at the tab. */
export function onceReaderReturns(onReturn: () => void): () => void {
  function stop() {
    window.removeEventListener('focus', check)
    document.removeEventListener('visibilitychange', check)
  }
  function check() {
    if (isReaderAway()) return
    stop()
    onReturn()
  }
  window.addEventListener('focus', check)
  document.addEventListener('visibilitychange', check)
  return stop
}

export interface DesktopNotice {
  title: string
  body: string
  /** A later notice with the same tag replaces this one instead of stacking, from any tab of the app. */
  tag: string
  /** Runs once the click has brought the tab forward. */
  onClick?: () => void
}

/** Shows the notice if the reader allowed notifications and is away from the tab; returns whether it did. */
export function notifyWhileAway({ title, body, tag, onClick }: DesktopNotice): boolean {
  if (getNotificationPermission() !== NotificationPermissionState.GRANTED || !isReaderAway()) return false
  let notification: Notification
  try {
    notification = new Notification(title, { body, tag })
  } catch {
    return false
  }
  // Back at the tab, the reader has the news on the page and the notice about it is stale.
  const stopWaiting = onceReaderReturns(() => notification.close())
  notification.onclick = () => {
    stopWaiting()
    window.focus()
    notification.close()
    onClick?.()
  }
  return true
}
